import React, { memo, useMemo } from 'react'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import { invoiceActionLabels } from 'src/constants/invoice/constants'
import { InvoiceActionVerb } from 'src/constants/invoice/enums'
import type { MenuActionItem } from 'src/interfaces/ui'

interface Props {
  verbs: InvoiceActionVerb[]
  /**
   * Payment-release entries, listed after the money verbs. Only a partner invoice row passes
   * them — a hold belongs to one partner invoice, so the composed row has none.
   */
  paymentReleaseItems?: MenuActionItem[]
  dataTestId: string
  onSelect(verb: InvoiceActionVerb): void
}

// Module-level so the default keeps one reference and `items` is not rebuilt every render.
const NO_PAYMENT_RELEASE_ITEMS: MenuActionItem[] = []

// An overflow menu rather than a row of buttons: there are up to seven verbs, and a row of
// seven destructive-looking buttons in a grid cell is both unreadable and easy to misclick.
const InvoiceActionsMenuComponent = ({
  verbs,
  paymentReleaseItems = NO_PAYMENT_RELEASE_ITEMS,
  dataTestId,
  onSelect,
}: Props) => {
  const items = useMemo<MenuActionItem[]>(() => {
    const verbItems = verbs.map((verb) => {
      return {
        label: invoiceActionLabels[verb],
        onClick: () => {
          onSelect(verb)
        },
      }
    })

    return [...verbItems, ...paymentReleaseItems]
  }, [verbs, paymentReleaseItems, onSelect])

  return <RowActionsMenu items={items} ariaLabel="Invoice actions" dataTestId={dataTestId} />
}

export const InvoiceActionsMenu = memo(InvoiceActionsMenuComponent)
